import { Effect } from 'effect';
import { normalizeStructuredSchemaEffect, type StructuredSchemaError } from '../structured-schema';
import type { StructuredSchema } from '../types';

type RpcArgObject = Record<string, unknown> & {
  schema?: unknown;
};

export function normalizeOkraRpcArgs(args: readonly unknown[]): unknown[] {
  const outcome = Effect.runSync(
    Effect.match(normalizeOkraRpcArgsEffect(args), {
      onFailure: (error) => ({ success: false as const, error }),
      onSuccess: (normalized) => ({ success: true as const, normalized }),
    }),
  );
  if (outcome.success) return outcome.normalized;
  throw new Error(
    `Okra RPC schema normalization failed: ${outcome.error.issues.join('; ')} Pass JSON Schema directly or use a Zod version supported by @okrapdf/sdk.`,
  );
}

export function normalizeOkraRpcArgsEffect(
  args: readonly unknown[],
): Effect.Effect<unknown[], StructuredSchemaError> {
  return Effect.gen(function* () {
    const normalized: unknown[] = [];
    for (const arg of args) {
      if (!isRpcArgObject(arg) || arg.schema === undefined) {
        normalized.push(arg);
        continue;
      }
      const schema = yield* normalizeRpcSchemaEffect(arg.schema);
      normalized.push({ ...arg, schema });
    }
    return normalized;
  });
}

function normalizeRpcSchemaEffect(schema: unknown): Effect.Effect<unknown, StructuredSchemaError> {
  if (!isStructuredSchemaLike(schema)) return Effect.succeed(schema);
  return normalizeStructuredSchemaEffect(schema as StructuredSchema<unknown>).pipe(
    Effect.map((normalized) => normalized.jsonSchema),
  );
}

function isStructuredSchemaLike(value: unknown): value is StructuredSchema<unknown> {
  return !!value && typeof value === 'object';
}

function isRpcArgObject(value: unknown): value is RpcArgObject {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}
